/**
 * CommunityUserProfilePage — premium public profile (viewed by other users).
 *
 * Privacy guarantee: this page renders ONLY data returned by the public
 * `/api/community/profile/{userId}` endpoint, which strips email, exact
 * createdAt timestamp, and other private fields when viewer !== target.
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Shield, Crown, ExternalLink, ArrowLeft, TrendingUp, Calendar,
  Award, Loader2, CheckCircle, Lock, Users, MessageSquare,
  UserPlus, UserMinus, Sparkles, Target, BadgeCheck, Edit2, Twitter, Globe,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getCommunityProfile, followUser, unfollowUser, checkFollowing } from '../services/api';

const AVATAR_COLORS = [
  'from-rose-500 to-pink-600', 'from-purple-500 to-fuchsia-600',
  'from-violet-500 to-indigo-600', 'from-blue-500 to-cyan-600',
  'from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600',
];

function avatarGradient(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

const ROLE_BADGE = {
  owner:      { label: 'Owner',      cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
  superadmin: { label: 'S.Admin',    cls: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  admin:      { label: 'Admin',      cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  mod:        { label: 'Mod',        cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  vip:        { label: 'VIP',        cls: 'bg-violet-500/20 text-violet-400 border-violet-500/30' },
};

const TIER_BADGE = {
  trial:   { cls: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  starter: { cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  trader:  { cls: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30' },
  pro:     { cls: 'bg-violet-500/15 text-violet-400 border-violet-500/30' },
  elite:   { cls: 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/30' },
};

export default function CommunityUserProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const isSelf = currentUser?.userId === userId;
  // Stable string dep — see CommunityProfilePage for the rationale.
  const currentUid = currentUser?.userId;

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);

    const viewerIsSelf = currentUid === userId;

    // No cancelled flag — see CommunityProfilePage for why. Letting late
    // setStates through prevents the silent-swallow bug where successful
    // responses were discarded mid-effect-rerun.
    getCommunityProfile(userId)
      .then(p => {
        if (!p) { setError('Profile not found'); return; }
        setProfile(p);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));

    if (!viewerIsSelf) {
      checkFollowing(userId)
        .then(f => setIsFollowing(!!(f?.isFollowing || f?.following)))
        .catch(() => {});
    }
  }, [userId, currentUid]);

  async function toggleFollow() {
    if (isSelf) return;
    setFollowBusy(true);
    try {
      if (isFollowing) await unfollowUser(userId);
      else await followUser(userId);
      setIsFollowing(!isFollowing);
      // Refresh just the follower count — don't re-fetch everything (and
      // don't put the page back in a loading state).
      getCommunityProfile(userId)
        .then(p => { if (p) setProfile(p); })
        .catch(() => {});
    } catch (e) {
      // optimistic-safe — swallow
    }
    setFollowBusy(false);
  }

  // Render the back-link + an inline loader while loading; the rest of the
  // page only renders once we actually have profile data. No full-page guard
  // so a hung request can't trap the user on a spinner.
  if (loading && !profile) {
    return (
      <div className="space-y-4 max-w-4xl">
        <Link to="/community" className="text-sm text-gray-500 hover:text-white flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Community
        </Link>
        <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
          <span className="text-sm text-gray-400">Loading profile…</span>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">{error || 'Profile not found'}</p>
        <Link to="/community" className="text-accent-primary text-sm mt-2 inline-block">← Back to Community</Link>
      </div>
    );
  }

  const p = profile;
  const badges = p.badges || [];
  const achievements = p.achievements || [];
  const earned = achievements.filter(a => a.earned);
  const remaining = achievements.filter(a => !a.earned);
  const gradient = avatarGradient(p.name);
  const tierCls = TIER_BADGE[p.tier]?.cls || TIER_BADGE.starter.cls;
  const roleBadge = ROLE_BADGE[p.chatRole];
  const callStats = p.stats?.callStats;
  const memberSinceLabel = p.stats?.memberSince
    ? new Date(p.stats.memberSince).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      <Link to="/community" className="text-sm text-gray-500 hover:text-white flex items-center gap-1 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Community
      </Link>

      {/* ─── Premium Hero ────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-dark-700/50">
        {/* Gradient cover */}
        <div className={`h-32 bg-gradient-to-br ${gradient} relative`}>
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/40 to-transparent" />
        </div>
        <div className="bg-dark-800 px-6 pb-6 -mt-16 relative">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            {/* Avatar + identity */}
            <div className="flex items-end gap-4">
              {p.avatar ? (
                <img src={p.avatar} alt={p.name}
                  className="w-28 h-28 rounded-2xl border-4 border-dark-800 shadow-2xl object-cover" />
              ) : (
                <div className={`w-28 h-28 rounded-2xl border-4 border-dark-800 shadow-2xl bg-gradient-to-br ${gradient} flex items-center justify-center text-4xl font-bold text-white`}>
                  {(p.name || '?')[0].toUpperCase()}
                </div>
              )}
              <div className="pb-2">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <h1 className="text-2xl font-bold text-white">{p.name}</h1>
                  {callStats?.isVerifiedCaller && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                      <BadgeCheck className="w-3 h-3" /> VERIFIED CALLER
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${tierCls}`}>
                    {p.tier || 'starter'}
                  </span>
                  {roleBadge && (
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${roleBadge.cls}`}>
                      {roleBadge.label}
                    </span>
                  )}
                  <span className="text-[10px] text-gray-500 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Member since {memberSinceLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 pb-2">
              {isSelf ? (
                <button onClick={() => navigate('/community/profile')}
                  className="px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold flex items-center gap-1.5">
                  <Edit2 className="w-3.5 h-3.5" /> Edit profile
                </button>
              ) : (
                <button onClick={toggleFollow} disabled={followBusy}
                  className={`px-5 py-2 rounded-xl text-sm font-bold flex items-center gap-1.5 transition-all disabled:opacity-50 ${
                    isFollowing
                      ? 'bg-dark-700 hover:bg-dark-600 text-gray-300 border border-dark-600'
                      : 'bg-violet-500 hover:bg-violet-400 text-white'
                  }`}>
                  {followBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                   isFollowing ? <><UserMinus className="w-3.5 h-3.5" /> Following</> :
                   <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
                </button>
              )}
            </div>
          </div>

          {/* Bio */}
          {p.bio && (
            <p className="text-sm text-gray-300 mt-4 leading-relaxed max-w-2xl">{p.bio}</p>
          )}

          {/* Social links */}
          {(p.twitter || p.website) && (
            <div className="flex gap-3 mt-3">
              {p.twitter && (
                <a href={`https://twitter.com/${p.twitter}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1">
                  <Twitter className="w-3.5 h-3.5" /> @{p.twitter}
                </a>
              )}
              {p.website && (
                <a href={p.website.startsWith('http') ? p.website : `https://${p.website}`} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-gray-400 hover:text-gray-300 inline-flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" /> {p.website}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── KPI Strip ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Users} label="Followers" value={p.stats?.followerCount || 0} color="violet" />
        <KPI icon={UserPlus} label="Following" value={p.stats?.followingCount || 0} color="cyan" />
        <KPI icon={MessageSquare} label="Messages" value={p.stats?.messageCount || 0} color="emerald" />
        <KPI icon={Sparkles} label="Reputation" value={p.stats?.reputation || 0} color="amber" />
      </div>

      {/* ─── Verified Caller Stats ────────────────────────────────────── */}
      {callStats && callStats.totalCalls > 0 && (
        <div className="bg-gradient-to-br from-emerald-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-emerald-500/20 p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              <h3 className="text-sm font-bold text-white">Trading Call Performance</h3>
              {callStats.isVerifiedCaller && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  VERIFIED CALLER ✓
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500">{callStats.resolvedCalls} of {callStats.totalCalls} calls resolved</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] text-gray-500 uppercase">Total Calls</p>
              <p className="text-2xl font-bold text-white">{callStats.totalCalls}</p>
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] text-gray-500 uppercase">Accuracy</p>
              <p className="text-2xl font-bold text-emerald-400">
                {callStats.accuracy != null ? `${callStats.accuracy.toFixed(0)}%` : '—'}
              </p>
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3">
              <p className="text-[10px] text-gray-500 uppercase">Avg % Gain</p>
              <p className={`text-2xl font-bold ${(callStats.avgPercentGain || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {callStats.avgPercentGain != null ? `${callStats.avgPercentGain >= 0 ? '+' : ''}${callStats.avgPercentGain.toFixed(1)}%` : '—'}
              </p>
            </div>
          </div>
          {!callStats.isVerifiedCaller && callStats.resolvedCalls < 20 && (
            <p className="text-[11px] text-gray-500 mt-3">
              Needs {20 - callStats.resolvedCalls} more resolved calls @ 60%+ accuracy for the Verified Caller badge.
            </p>
          )}
        </div>
      )}

      {/* ─── Badges ───────────────────────────────────────────────────── */}
      {badges.length > 0 && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Verification Badges
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {badges.map(b => (
              <div key={b.id} className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 rounded-xl p-3">
                <span className="text-2xl block mb-1">{b.emoji}</span>
                <p className="text-xs font-bold text-white">{b.label}</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Achievements ──────────────────────────────────────────────── */}
      {achievements.length > 0 && (
        <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <Award className="w-4 h-4" /> Achievements
            </h3>
            <p className="text-[11px] text-gray-500">
              {earned.length} of {achievements.length} unlocked
            </p>
          </div>
          {earned.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-4">
              {earned.map(a => (
                <div key={a.id} className="bg-violet-500/10 border border-violet-500/30 rounded-xl p-3 text-center" title={a.description || ''}>
                  <span className="text-3xl block">{a.emoji}</span>
                  <p className="text-[10px] font-semibold text-white mt-1.5">{a.name}</p>
                </div>
              ))}
            </div>
          )}
          {remaining.length > 0 && (
            <>
              <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2">Locked</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {remaining.slice(0, 12).map(a => (
                  <div key={a.id} className="bg-dark-700/30 border border-dark-700/50 rounded-xl p-3 text-center opacity-40" title={a.description || ''}>
                    <span className="text-3xl block grayscale">{a.emoji}</span>
                    <p className="text-[10px] font-semibold text-gray-500 mt-1.5">{a.name}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, color = 'violet' }) {
  const colors = {
    violet:  'from-violet-500/10 to-purple-500/10 border-violet-500/20 text-violet-400',
    cyan:    'from-cyan-500/10 to-blue-500/10 border-cyan-500/20 text-cyan-400',
    emerald: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20 text-emerald-400',
    amber:   'from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-400',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-2xl p-4 border`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}
